import type { Graph, Thing, WithContext } from 'schema-dts';

type JsonLdProps = {
  json: Graph | WithContext<Thing> | readonly WithContext<Thing>[];
  'data-testid'?: string;
};

const UNSAFE_JSON_CHARACTERS = /[&<>\u007f-\uffff]/g;

function toUnicodeEscape(character: string): string {
  return `\\u${character.charCodeAt(0).toString(16).padStart(4, '0')}`;
}

/**
 * Like `JSON.stringify`, but escapes any potentially unsafe characters for embedding in an HTML <script> tag.
 *
 * @see https://nextjs.org/docs/app/guides/json-ld
 * @see https://github.com/KablamoOSS/kerosene/blob/master/packages/kerosene/src/string/domSafeJson.ts
 */
function domSafeJson(value: unknown): string {
  return JSON.stringify(value).replace(UNSAFE_JSON_CHARACTERS, toUnicodeEscape);
}

function JsonLd({ json, 'data-testid': dataTestId = 'json-ld' }: JsonLdProps) {
  return <script type="application/ld+json" data-testid={dataTestId} dangerouslySetInnerHTML={{ __html: domSafeJson(json) }} />;
}

export { JsonLd, type JsonLdProps };
